import { useNavigate } from "react-router";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Ban, CheckCircle2, ExternalLink, Trash2, XCircle } from "lucide-react";
import { TableCell, TableRow, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@bookdental/ui";
import { cn } from "@/lib/cn";
import { dataDeNascimento, idadeEmAnos } from "@/lib/nascimento";
import { getPatientColor, patientGradient } from "@/lib/patient-color";

export type PatientRowShape = {
  id: string;
  name: string;
  identifier: string | null;
  document: string | null;
  sex: string | null;
  birthDate: Date | string | null;
  phone: string | null;
  email: string | null;
  avatarUrl: string | null;
  tags: string[];
  active: boolean;
  city: string | null;
  state: string | null;
  createdAt?: Date | string;
};

export function formatPhone(v: string | null) {
  if (!v) return "";
  const d = v.replace(/\D/g, "");
  if (d.length === 11) return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
  if (d.length === 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return v;
}

export function formatCpf(v: string | null) {
  if (!v) return "";
  const d = v.replace(/\D/g, "");
  if (d.length !== 11) return v;
  return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
}

export const age = idadeEmAnos;

/** Monta URL wa.me com DDI 55 + mensagem pré-escrita com primeiro nome. */
export function buildWhatsAppUrl(phone: string, fullName: string): string {
  const digits = phone.replace(/\D/g, "");
  const withCountry = digits.startsWith("55") ? digits : `55${digits}`;
  const firstName = fullName.trim().split(/\s+/)[0] ?? "";
  const message = encodeURIComponent(
    firstName ? `Olá ${firstName}, aqui é da clínica.` : "Olá, aqui é da clínica.",
  );
  return `https://wa.me/${withCountry}?text=${message}`;
}

type Props = {
  patient: PatientRowShape;
  index: number;
  onRemove: (p: PatientRowShape) => void;
  onToggleActive: (p: PatientRowShape) => void;
};

export function PatientListRow({ patient: p, index, onRemove, onToggleActive }: Props) {
  const navigate = useNavigate();
  const initial = p.name.charAt(0).toUpperCase();
  const color = getPatientColor(p.name);
  const years = age(p.birthDate);
  const birthFmt = dataDeNascimento(p.birthDate);
  const createdFmt = p.createdAt ? format(new Date(p.createdAt), "dd/MM/yyyy", { locale: ptBR }) : null;

  const handleRowClick = () => navigate(`/pacientes/${p.id}`);

  return (
    <TableRow
      onClick={handleRowClick}
      className={cn(
        "cursor-pointer text-[13px] transition-colors hover:bg-zinc-50",
        !p.active && "opacity-60",
      )}
      style={{ height: 52 }}
    >
      <TableCell className="hidden sm:table-cell py-1 px-2 text-center text-zinc-400 text-[11px] font-medium">
        {index + 1}
      </TableCell>

      <TableCell className="py-1 px-2 text-center">
        {p.active ? (
          <CheckCircle2 className="mx-auto h-4 w-4 text-emerald-500" />
        ) : (
          <XCircle className="mx-auto h-4 w-4 text-red-400" />
        )}
      </TableCell>

      <TableCell className="py-1 px-2">
        <div className="flex items-center gap-2.5 min-w-0">
          {p.avatarUrl ? (
            <img
              src={p.avatarUrl}
              alt=""
              className="h-7 w-7 shrink-0 rounded-full object-cover"
              style={{ boxShadow: `0 2px 6px ${color.solid}33` }}
            />
          ) : (
            <div
              className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold text-white"
              style={{
                background: patientGradient(color),
                boxShadow: `0 2px 6px ${color.solid}40`,
              }}
            >
              {initial}
            </div>
          )}
          <div className="min-w-0">
            <p className="truncate font-medium text-zinc-900">{p.name}</p>
            <p className="truncate text-[10px] text-zinc-400 sm:hidden">
              {[formatCpf(p.document), formatPhone(p.phone)].filter(Boolean).join(" • ") || p.identifier || ""}
            </p>
            {p.identifier && (
              <p className="hidden truncate text-[10px] text-zinc-400 sm:block">{p.identifier}</p>
            )}
          </div>
        </div>
      </TableCell>

      <TableCell className="hidden sm:table-cell py-1 px-2 text-center text-[12px] text-zinc-700 whitespace-nowrap">
        {formatCpf(p.document) || "—"}
      </TableCell>

      <TableCell className="hidden md:table-cell py-1 px-2 text-center text-[12px] text-zinc-700 truncate max-w-[200px]">
        {p.email || "—"}
      </TableCell>

      <TableCell className="hidden md:table-cell py-1 px-2 text-center text-[12px] text-zinc-700 whitespace-nowrap">
        {formatPhone(p.phone) || "—"}
      </TableCell>

      <TableCell className="hidden lg:table-cell py-1 px-2 text-center text-[12px] text-zinc-700 whitespace-nowrap">
        {birthFmt ? (
          <span>
            {birthFmt}
            {years !== null && (
              <span className="ml-1 text-zinc-400">({years}a)</span>
            )}
          </span>
        ) : (
          "—"
        )}
      </TableCell>

      <TableCell className="hidden lg:table-cell py-1 px-2 text-center">
        <div className="flex flex-wrap justify-center gap-1">
          {p.tags.slice(0, 3).map((t) => {
            const tc = getPatientColor(t);
            return (
              <span
                key={t}
                className="rounded-full px-1.5 py-0.5 text-[10px] font-medium"
                style={{
                  background: `${tc.solid}22`,
                  color: tc.to,
                }}
              >
                {t}
              </span>
            );
          })}
          {p.tags.length > 3 && (
            <span className="text-[10px] text-zinc-400">+{p.tags.length - 3}</span>
          )}
        </div>
      </TableCell>

      <TableCell className="hidden xl:table-cell py-1 px-2 text-center text-[11px] text-zinc-500 whitespace-nowrap">
        {createdFmt || "—"}
      </TableCell>

      <TableCell className="py-1 px-2 text-center">
        <div className="flex items-center justify-center gap-0.5" onClick={(e) => e.stopPropagation()}>
          <TooltipProvider delayDuration={300}>
            {p.phone && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <a
                    href={buildWhatsAppUrl(p.phone, p.name)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex h-7 w-7 items-center justify-center rounded-md text-zinc-500 transition-colors hover:bg-emerald-50 hover:text-emerald-600 dark:hover:bg-emerald-500/10 dark:hover:text-emerald-400"
                    aria-label={`Enviar WhatsApp para ${p.name}`}
                  >
                    <WhatsAppIcon className="h-4 w-4" />
                  </a>
                </TooltipTrigger>
                <TooltipContent>WhatsApp</TooltipContent>
              </Tooltip>
            )}
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={handleRowClick}
                  className="inline-flex h-7 w-7 items-center justify-center rounded-md text-zinc-500 hover:bg-brand-50 hover:text-brand-700"
                >
                  <ExternalLink className="h-4 w-4" />
                </button>
              </TooltipTrigger>
              <TooltipContent>Ver ficha</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={() => onToggleActive(p)}
                  className={cn(
                    "inline-flex h-7 w-7 items-center justify-center rounded-md text-zinc-500",
                    p.active
                      ? "hover:bg-amber-50 hover:text-amber-600"
                      : "hover:bg-emerald-50 hover:text-emerald-600",
                  )}
                >
                  {p.active ? <Ban className="h-4 w-4" /> : <CheckCircle2 className="h-4 w-4" />}
                </button>
              </TooltipTrigger>
              <TooltipContent>{p.active ? "Inativar" : "Reativar"}</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={() => onRemove(p)}
                  className="inline-flex h-7 w-7 items-center justify-center rounded-md text-zinc-500 hover:bg-red-50 hover:text-red-600"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </TooltipTrigger>
              <TooltipContent>Excluir</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </TableCell>
    </TableRow>
  );
}

/** Logo oficial do WhatsApp (currentColor — fica preto/branco conforme
 *  a cor do texto do botão). */
function WhatsAppIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="currentColor"
      className={className}
      aria-hidden="true"
    >
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
    </svg>
  );
}
